/**
 * Make horizontally-scrollable tables reachable from a keyboard.
 *
 * A wide table sits in a wrapper with `overflow-x: auto`, so a mouse or touch
 * user can swipe the rest of the columns into view. A keyboard user cannot:
 * nothing inside the wrapper is focusable, and a scroll container that never
 * receives focus never receives arrow keys either. On the career guides that
 * hides 137–150px of every table at phone width — real content, unreachable
 * (WCAG 2.1.1 Keyboard).
 *
 * `tabindex="0"` is the whole fix: it puts the container in the tab order, and
 * the browser scrolls a focused container with the arrow keys for free.
 *
 * Two details worth keeping:
 *
 * - It is applied only while the table actually overflows. A table that fits
 *   is not a scroll container, and a tab stop that does nothing is its own
 *   small accessibility problem — so this runs again on resize.
 * - A focusable container is announced as a `region` only when we can give it
 *   a real name (its caption, or the heading it sits under). An unnamed
 *   landmark is noise; without a name we leave the role off and let the table
 *   inside speak for itself.
 */

const SELECTOR = '.wp-block-table, .table-block__table';

/** Caption first, then the heading the table sits under. '' when neither. */
function nameFor(wrap) {
	const caption = wrap.querySelector('caption');
	if (caption && caption.textContent.trim()) {
		return caption.textContent.trim();
	}

	let node = wrap.previousElementSibling;
	while (node) {
		if (/^H[1-6]$/.test(node.tagName) && node.textContent.trim()) {
			return node.textContent.trim();
		}
		node = node.previousElementSibling;
	}

	return '';
}

function sync(wrap) {
	// A 1px tolerance: sub-pixel layout rounding otherwise reports a table that
	// fits exactly as scrollable, and it would collect a pointless tab stop.
	const scrolls = wrap.scrollWidth > wrap.clientWidth + 1;

	if (scrolls) {
		if (!('ggScrollRegion' in wrap.dataset)) {
			wrap.dataset.ggScrollRegion = '';
			wrap.setAttribute('tabindex', '0');
			const name = nameFor(wrap);
			if (name && !wrap.hasAttribute('aria-label')) {
				wrap.setAttribute('role', 'region');
				wrap.setAttribute('aria-label', name);
			}
		}
		return;
	}

	// Only ever remove what we added — an author's own role/label stays put.
	if ('ggScrollRegion' in wrap.dataset) {
		delete wrap.dataset.ggScrollRegion;
		wrap.removeAttribute('tabindex');
		wrap.removeAttribute('role');
		wrap.removeAttribute('aria-label');
	}
}

export default function initScrollRegions() {
	const wraps = Array.from(document.querySelectorAll(SELECTOR));
	if (!wraps.length) return;

	let raf = null;
	const syncAll = () => {
		if (raf) cancelAnimationFrame(raf);
		raf = requestAnimationFrame(() => wraps.forEach(sync));
	};

	/**
	 * Whether a table overflows is not settled at DOM ready, and checking once
	 * there is how this shipped broken: on production the tables measured as
	 * fitting when this first ran, grew when a webfont swapped in, and never
	 * got their tab stop — 148px of columns still unreachable. It worked
	 * locally only because the fonts were already cached.
	 *
	 * So watch the boxes rather than guess at the timing. The wrapper covers
	 * viewport changes; the table inside covers everything that changes its
	 * width without changing the wrapper's — font swaps, late images, an
	 * editor's live preview.
	 */
	if ('ResizeObserver' in window) {
		const ro = new ResizeObserver(syncAll);
		wraps.forEach((wrap) => {
			ro.observe(wrap);
			const table = wrap.querySelector('table');
			if (table) ro.observe(table);
		});
	} else {
		window.addEventListener('resize', syncAll);
	}

	// Belt and braces for the two moments most likely to change the metrics.
	if (document.fonts && document.fonts.ready) document.fonts.ready.then(syncAll);
	window.addEventListener('load', syncAll);

	syncAll();
}
